import React, { useState, useMemo } from "react";
import i18n from "i18next";
import styled from "styled-components";
import { ReactComponent as ClearIcon } from "assets/images/delete_user_icon.svg";
import {
  planningVariables,
  formatNumberWithCurrency,
  getTickerUsingId,
  custodianSelector,
  getCustodianCost,
  currentPortfolioSelector,
  getTickerUsingShortName
} from "@kubera/common";
import PercentageInput from "./PercentageInput";
import CurrencyInput from "./CurrencyInput";
import DropDown from "./DropDown";
import PrimaryButton from "components/button/PrimaryButton";
import { useSelector } from "react-redux";

const Container = styled.div`
  margin-top: ${props => props.marginTop};
`;

const EstimatedTaxLabel = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 130%;
  text-decoration-line: underline;
  font-feature-settings: "ss01" on;
  color: ${props => props.theme.linkColor};
  cursor: pointer;
  width: fit-content;
`;

const FirstTitleContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputTitle = styled.div`
  margin-top: 35px;
  font-style: normal;
  font-weight: 700;
  font-size: 22px;
  line-height: 130%;
  font-feature-settings: "ss01" on;
  color: #000000;
`;

const FirstTitle = styled(InputTitle)`
  margin-top: 0;
  flex: 1;
`;

const HitArea = styled.div`
  position: relative;
  height: 20px;
  width: 20px;
  cursor: pointer;
`;

const ClearButton = styled(ClearIcon)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

const RateInput = styled(PercentageInput)`
  width: 100%;
  margin-top: ${props => (props.lessMargin ? "0px" : "8px")};
`;

const Description = styled.div`
  font-size: 11px;
  opacity: 0.5;
  margin-top: 14px;
`;

const SuggestedCostContainer = styled.div`
  font-size: 11px;
  opacity: 0.5;
  margin-top: 14px;
`;

const ClickableAmount = styled.span`
  text-decoration: underline;
  cursor: pointer;
  opacity: 1;
`;

const AmountInput = styled(CurrencyInput)`
  margin-top: ${props => (props.lessMargin ? "0px" : "8px")};
`;

const TaxDeductionDropDown = styled(DropDown)`
  margin-top: 35px;
  background: transparent;
  padding: 0;
  border: 0;
`;

const SaveButton = styled(PrimaryButton)`
  margin-top: 35px;
`;

const CurrencyInputStyle = { display: "flex", flex: 1, height: "45px", border: "1px solid rgba(0, 0, 0, 0.4);" };

const ExpandingTaxInput = ({
  tax,
  taxDeductionAmount = { value: null, tickerId: null },
  taxDeductionRate = { value: null },
  costForTax,
  assetIds,
  defaultVar = planningVariables.TAX_DEDUCTION_AMOUNT,
  deductionDescriptionNoun = "yourIncome",
  updateParams,
  portfolioCurrency,
  tickerId,
  onGainTitle,
  marginTop = "35px",
  dropdownWidth = 360,
  collapsedTitle = "estimatedTax",
  onSaveClick
}) => {
  const isAmountDefault = defaultVar === planningVariables.TAX_DEDUCTION_AMOUNT;
  const secondVar = isAmountDefault ? planningVariables.TAX_DEDUCTION_RATE : planningVariables.TAX_DEDUCTION_AMOUNT;
  const [primaryOption, secondaryOption] = isAmountDefault
    ? [taxDeductionAmount, taxDeductionRate]
    : [taxDeductionRate, taxDeductionAmount];

  const [expand, setExpand] = useState(!isNaN(tax) && tax > 0);
  const [dropdownValue, setDropdownValue] = useState(
    primaryOption.value === null && secondaryOption.value !== null ? secondVar : defaultVar
  );
  const [forceSingleCell, setForceSingleCell] = useState(true); // cell mode is SELECT instead of edit when suggestion is clicked

  const targetAssetId = assetIds?.items?.length > 0 ? assetIds.items[0].id : null;
  const currentPortfolio = useSelector(currentPortfolioSelector);
  const custodianObjSelector = useMemo(
    () => state => {
      if (!targetAssetId || !currentPortfolio) return null;
      return custodianSelector(state, targetAssetId, currentPortfolio.id);
    },
    [targetAssetId, currentPortfolio]
  );
  const custodianObj = useSelector(custodianObjSelector);

  let suggestedCost = { value: null, tickerId: null };
  if (custodianObj && currentPortfolio) {
    const { cost, costExchangeRate, costTickerId } = custodianObj;
    if (cost !== null && !isNaN(cost) && costExchangeRate && costTickerId) {
      const costValue = getCustodianCost(
        cost,
        costExchangeRate,
        getTickerUsingId(costTickerId).shortName,
        currentPortfolio.currency
      );
      suggestedCost = {
        value: costValue,
        tickerId: getTickerUsingShortName(currentPortfolio.currency).id
      };
    }
  }

  const handleClear = () => {
    setExpand(false);
    setForceSingleCell(true);
    const costForTaxObj = costForTax ? { [planningVariables.COST_FOR_TAX]: { value: null, tickerId: null } } : {};
    updateParams({
      [planningVariables.TAX]: 0,
      [planningVariables.TAX_DEDUCTION_AMOUNT]: { value: null, tickerId: null },
      [planningVariables.TAX_DEDUCTION_RATE]: { value: null },
      ...costForTaxObj
    });
  };

  const handleDropdownSelection = item => {
    setDropdownValue(item.id);
  };

  const getDropdownData = () => [
    {
      id: planningVariables.TAX_DEDUCTION_AMOUNT,
      label: i18n.t("taxDeductionAmount"),
      selected: dropdownValue === planningVariables.TAX_DEDUCTION_AMOUNT
    },
    {
      id: planningVariables.TAX_DEDUCTION_RATE,
      label: i18n.t("taxDeductionRate"),
      selected: dropdownValue === planningVariables.TAX_DEDUCTION_RATE
    }
  ];
  return (
    tax !== undefined && (
      <Container marginTop={marginTop}>
        {!expand && <EstimatedTaxLabel onClick={() => setExpand(true)}>{i18n.t(collapsedTitle)}</EstimatedTaxLabel>}
        {expand && (
          <>
            {costForTax && (
              <>
                <FirstTitleContainer>
                  <FirstTitle>{i18n.t("cost")}</FirstTitle>
                  <HitArea onClick={handleClear}>
                    <ClearButton />
                  </HitArea>
                </FirstTitleContainer>
                <AmountInput
                  portfolioCurrency={portfolioCurrency}
                  value={costForTax.value}
                  tickerId={tickerId}
                  onChange={val =>
                    updateParams({
                      [planningVariables.COST_FOR_TAX]: val
                    })
                  }
                  inputStyle={CurrencyInputStyle}
                  hideExchangeRateBubble={true}
                  isInSingleCellMode={forceSingleCell}
                  isDisabled={!tax}
                  placeholder={i18n.t("amount")}
                />
                {suggestedCost && suggestedCost.value !== null && (
                  <SuggestedCostContainer>
                    {i18n.t("suggested")}:{" "}
                    <ClickableAmount
                      onClick={() => {
                        updateParams({
                          [planningVariables.COST_FOR_TAX]: suggestedCost
                        });
                        setForceSingleCell(false);
                      }}
                    >
                      {formatNumberWithCurrency(
                        Math.kuberaFloor(suggestedCost.value),
                        getTickerUsingId(suggestedCost.tickerId).shortName
                      )}
                    </ClickableAmount>
                  </SuggestedCostContainer>
                )}
              </>
            )}
            <>
              {costForTax ? (
                <InputTitle>{onGainTitle ? i18n.t("taxRateOnGain") : i18n.t("taxRate")}</InputTitle>
              ) : (
                <FirstTitleContainer>
                  <FirstTitle>{onGainTitle ? i18n.t("taxRateOnGain") : i18n.t("taxRate")}</FirstTitle>
                  <HitArea onClick={handleClear}>
                    <ClearButton />
                  </HitArea>
                </FirstTitleContainer>
              )}
              <RateInput
                value={tax}
                onChange={(e, parsedInput) => updateParams({ [planningVariables.TAX]: parsedInput })}
                callOnChangeOnClear={true}
                inputValidation={value => value < 0 || value > 100}
                placeholder={i18n.t("rate")}
              />
            </>
            <TaxDeductionDropDown
              width={dropdownWidth}
              selectedItemStyle={{ fontWeight: "bold", fontSize: "22px", flex: "inherit" }}
              items={getDropdownData()}
              onSelection={handleDropdownSelection}
            />
            {dropdownValue === planningVariables.TAX_DEDUCTION_AMOUNT ? (
              <AmountInput
                portfolioCurrency={portfolioCurrency}
                value={taxDeductionAmount.value}
                tickerId={tickerId}
                onChange={val =>
                  updateParams({
                    [planningVariables.TAX_DEDUCTION_AMOUNT]: val,
                    [planningVariables.TAX_DEDUCTION_RATE]: { value: null }
                  })
                }
                inputStyle={CurrencyInputStyle}
                hideExchangeRateBubble={true}
                isInSingleCellMode={true}
                isDisabled={!tax}
                lessMargin={true}
                placeholder={i18n.t("amount")}
              />
            ) : (
              <RateInput
                value={taxDeductionRate.value}
                onChange={(e, parsedInput) =>
                  updateParams({
                    [planningVariables.TAX_DEDUCTION_RATE]: { value: parsedInput },
                    [planningVariables.TAX_DEDUCTION_AMOUNT]: { value: null, tickerId: null }
                  })
                }
                callOnChangeOnClear={true}
                inputValidation={value => value < 0 || value > 100}
                isDisabled={!tax}
                lessMargin={true}
                placeholder={i18n.t("rate")}
              />
            )}
            <Description
              dangerouslySetInnerHTML={{
                __html: i18n
                  .t(
                    dropdownValue === planningVariables.TAX_DEDUCTION_AMOUNT
                      ? "taxDeductionAmountDescription"
                      : "taxDeductionRateDescription"
                  )
                  .replaceAll("{noun}", i18n.t(deductionDescriptionNoun))
              }}
            />
            {!!onSaveClick && <SaveButton title={i18n.t("save")} onClick={onSaveClick} />}
          </>
        )}
      </Container>
    )
  );
};

export default ExpandingTaxInput;
